import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import Header from '../../components/ui/Header';
import CartItem from './components/CartItem';
import CartSummary from './components/CartSummary';
import WishlistSection from './components/WishlistSection';
import RecommendedItems from './components/RecommendedItems';
import EmptyCart from './components/EmptyCart';
import { useCart } from '../../context/CartContext';

const ShoppingCart = () => {
  const { items: cartContextItems, updateQuantity, removeFromCart, clearCart } = useCart();
  const [cartItems, setCartItems] = useState([]);
  const [wishlistItems, setWishlistItems] = useState([]);
  const [recommendedItems, setRecommendedItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [discount, setDiscount] = useState(0);

  // Mock data
  const mockCartItems = [
    {
      id: 1,
      name: "iPhone 15 Pro Max",
      category: "Smartphones",
      variant: "256GB, Natural Titanium",
      price: 1199.99,
      originalPrice: 1299.99,
      quantity: 1,
      stock: 15,
      image: "https://images.unsplash.com/photo-1592750475338-74b7b21085ab?w=400&h=400&fit=crop"
    },
    {
      id: 2,
      name: "MacBook Pro 16-inch M3",
      category: "Laptops",
      variant: "16GB RAM, 512GB SSD",
      price: 2499.99,
      quantity: 1,
      stock: 8,
      image: "https://images.unsplash.com/photo-1541807084-5c52b6b3adef?w=400&h=400&fit=crop"
    },
    {
      id: 3,
      name: "AirPods Pro (3rd Gen)",
      category: "Audio",
      price: 249.99,
      originalPrice: 279.99,
      quantity: 2,
      stock: 25,
      image: "https://images.unsplash.com/photo-1606220945770-b5b6c2c55bf1?w=400&h=400&fit=crop"
    }
  ];

  const mockWishlistItems = [
    {
      id: 4,
      name: "iPad Air 11-inch M2",
      category: "Tablets",
      price: 599.99,
      originalPrice: 649.99,
      stock: 12,
      image: "https://images.unsplash.com/photo-1544244015-0df4b3ffc6b0?w=400&h=400&fit=crop"
    },
    {
      id: 5,
      name: "Apple Watch Series 9",
      category: "Wearables",
      price: 399.99,
      stock: 20,
      image: "https://images.unsplash.com/photo-1434493789847-2f02dc6ca35d?w=400&h=400&fit=crop"
    }
  ];

  const mockRecommendedItems = [
    {
      id: 6,
      name: "MagSafe Charger",
      price: 39.99,
      originalPrice: 49.99,
      rating: 4.5,
      reviews: 1250,
      isNew: true,
      discount: 20,
      image: "https://images.unsplash.com/photo-1583394838336-acd977736f90?w=400&h=400&fit=crop"
    },
    {
      id: 7,
      name: "iPhone 15 Case",
      price: 49.99,
      rating: 4.8,
      reviews: 890,
      image: "https://images.unsplash.com/photo-1556656793-08538906a9f8?w=400&h=400&fit=crop"
    },
    {
      id: 8,
      name: "USB-C Cable",
      price: 19.99,
      originalPrice: 29.99,
      rating: 4.3,
      reviews: 2100,
      discount: 33,
      image: "https://images.unsplash.com/photo-1558618666-fcd25c85cd64?w=400&h=400&fit=crop"
    },
    {
      id: 9,
      name: "Wireless Mouse",
      price: 79.99,
      rating: 4.6,
      reviews: 650,
      isNew: true,
      image: "https://images.unsplash.com/photo-1527864550417-7fd91fc51a46?w=400&h=400&fit=crop"
    }
  ];

  useEffect(() => {
    // Simulate loading
    const timer = setTimeout(() => {
      setCartItems(cartContextItems?.length ? cartContextItems : mockCartItems);
      setWishlistItems(mockWishlistItems);
      setRecommendedItems(mockRecommendedItems);
      setLoading(false);
    }, 300);

    return () => clearTimeout(timer);
  }, [cartContextItems]);

  const handleUpdateQuantity = async (itemId, newQuantity) => {
    updateQuantity(itemId, newQuantity);
    setCartItems(prev => prev.map(item => item.id === itemId ? { ...item, quantity: newQuantity } : item));
  };

  const handleRemoveItem = async (itemId) => {
    removeFromCart(itemId);
    setCartItems(prev => prev?.filter(item => item?.id !== itemId));
  };

  const handleMoveToWishlist = async (itemId) => {
    const item = cartItems?.find(item => item?.id === itemId);
    if (item) {
      setWishlistItems(prev => [...prev, { ...item, quantity: 1 }]);
      setCartItems(prev => prev?.filter(item => item?.id !== itemId));
    }
  };

  const handleMoveToCart = async (itemId) => {
    const item = wishlistItems?.find(item => item?.id === itemId);
    if (item) {
      setCartItems(prev => [...prev, { ...item, quantity: 1 }]);
      setWishlistItems(prev => prev?.filter(item => item?.id !== itemId));
    }
  };

  const handleRemoveFromWishlist = async (itemId) => {
    setWishlistItems(prev => prev?.filter(item => item?.id !== itemId));
  };

  const handleAddToCart = async (itemId) => {
    const item = recommendedItems?.find(item => item?.id === itemId);
    if (item) {
      const existingItem = cartItems?.find(cartItem => cartItem?.id === itemId);
      if (existingItem) {
        handleUpdateQuantity(itemId, existingItem?.quantity + 1);
      } else {
        setCartItems(prev => [...prev, { ...item, quantity: 1, stock: 10 }]);
      }
    }
  };

  const handleApplyPromo = async (promoCode) => {
    // Mock promo codes
    const validPromoCodes = {
      'SAVE10': { discount: 0.1, message: '10% discount applied' },
      'WELCOME20': { discount: 0.2, message: '20% welcome discount applied' },
      'FREESHIP': { discount: 50, message: 'Free shipping applied' }
    };

    if (validPromoCodes?.[promoCode?.toUpperCase()]) {
      const promo = validPromoCodes?.[promoCode?.toUpperCase()];
      const subtotal = cartItems?.reduce((sum, item) => sum + (item?.price * item?.quantity), 0);
      const discountAmount = promo?.discount < 1 ? subtotal * promo?.discount : promo?.discount;
      setDiscount(discountAmount);
      return { success: true, message: promo?.message };
    }
    
    return { success: false, message: 'Invalid promo code' };
  };

  const shipping = cartItems?.reduce((sum, item) => sum + (item?.price * item?.quantity), 0) >= 50 ? 0 : 9.99;
  const tax = cartItems?.reduce((sum, item) => sum + (item?.price * item?.quantity), 0) * 0.08;

  if (loading) {
    return (
      <div className="min-h-screen bg-background">
        <Header />
        <div className="pt-16 flex items-center justify-center min-h-screen">
          <div className="text-center space-y-4">
            <div className="quantum-loader mx-auto"></div>
            <p className="text-muted-foreground">Loading your cart...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <Header />
      <main className="pt-16">
        <div className="container mx-auto px-6 py-8">
          {/* Page Header */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            className="mb-8"
          >
            <div className="flex items-center space-x-3 mb-2">
              <Icon name="ShoppingCart" size={32} className="text-primary" />
              <h1 className="text-3xl font-bold command-text">Shopping Cart</h1>
            </div>
            <p className="text-muted-foreground">
              Review your items and proceed to checkout
            </p>
          </motion.div>

          {cartItems?.length === 0 ? (
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.5 }}
            >
              <EmptyCart />
            </motion.div>
          ) : (
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
              {/* Cart Items */}
              <motion.div
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.5, delay: 0.1 }}
                className="lg:col-span-2 space-y-6"
              >
                <div className="flex items-center justify-between">
                  <h2 className="text-2xl font-bold command-text">
                    Cart Items ({cartItems?.length})
                  </h2>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => { clearCart(); setCartItems([]); }}
                    className="text-muted-foreground hover:text-error"
                  >
                    <Icon name="Trash2" size={16} className="mr-2" />
                    Clear Cart
                  </Button>
                </div>

                <div className="space-y-4">
                  {cartItems?.map((item, index) => (
                    <motion.div
                      key={item?.id}
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ duration: 0.3, delay: index * 0.1 }}
                    >
                      <CartItem
                        item={item}
                        onUpdateQuantity={handleUpdateQuantity}
                        onRemove={handleRemoveItem}
                        onMoveToWishlist={handleMoveToWishlist}
                      />
                    </motion.div>
                  ))}
                </div>
              </motion.div>

              {/* Cart Summary */}
              <motion.div
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.5, delay: 0.2 }}
                className="lg:col-span-1"
              >
                <div className="sticky top-24">
                  <CartSummary
                    items={cartItems}
                    shipping={shipping}
                    tax={tax}
                    discount={discount}
                    onApplyPromo={handleApplyPromo}
                  />
                </div>
              </motion.div>
            </div>
          )}

          {/* Wishlist Section */}
          {wishlistItems?.length > 0 && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.3 }}
              className="mt-16"
            >
              <WishlistSection
                wishlistItems={wishlistItems}
                onMoveToCart={handleMoveToCart}
                onRemoveFromWishlist={handleRemoveFromWishlist}
              />
            </motion.div>
          )}

          {/* Recommended Items */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.4 }}
            className="mt-16"
          >
            <RecommendedItems
              recommendedItems={recommendedItems}
              onAddToCart={handleAddToCart}
            />
          </motion.div>

          {/* Security Footer */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.5 }}
            className="mt-16 pt-8 border-t border-border"
          >
            <div className="flex flex-col sm:flex-row items-center justify-center space-y-4 sm:space-y-0 sm:space-x-8">
              <div className="flex items-center space-x-2">
                <div className="trust-seal p-2 bg-success/10 border border-success/30 rounded-lg">
                  <Icon name="Shield" size={20} color="var(--color-success)" />
                </div>
                <div className="text-sm">
                  <div className="font-medium">256-bit SSL Encryption</div>
                  <div className="text-muted-foreground">Your data is secure</div>
                </div>
              </div>
              <div className="flex items-center space-x-2">
                <div className="trust-seal p-2 bg-primary/10 border border-primary/30 rounded-lg">
                  <Icon name="CreditCard" size={20} color="var(--color-primary)" />
                </div>
                <div className="text-sm">
                  <div className="font-medium">Secure Payments</div>
                  <div className="text-muted-foreground">Multiple payment options</div>
                </div>
              </div>
              <div className="flex items-center space-x-2">
                <div className="trust-seal p-2 bg-secondary/10 border border-secondary/30 rounded-lg">
                  <Icon name="Truck" size={20} color="var(--color-secondary)" />
                </div>
                <div className="text-sm">
                  <div className="font-medium">Fast Shipping</div>
                  <div className="text-muted-foreground">Free on orders DZ 6,750+</div>
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </main>
      {/* Data Stream Animation */}
      <div className="data-stream"></div>
    </div>
  );
};

export default ShoppingCart;